import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import GoBack from 'components/actions/GoBack';
import { MdInsights, MdRefresh } from 'react-icons/md';

import KpiFilters from './components/KpiFilters';
import RevenueChart from './components/RevenueChart';
import TopProducts from './components/TopProducts';
import ProductMarginTable from './components/ProductMarginTable';
import ClientMarginTable from './components/ClientMarginTable';
import ProspectFunnel from './components/ProspectFunnel';
import UnpaidInvoices from './components/UnpaidInvoices';
import ParetoCard from './components/ParetoCard';
import { resolveQuickRange } from './components/helpers';
import { triggerBiRebuild, getBiStatus } from 'interfaces/bi';

/**
 * BI / KPI dashboard page.
 *
 * Assembles the eight KPI cards, all driven by a single shared period filter.
 * Every card reads from the pre-aggregated `/bi/*` endpoints, so the page stays
 * fast regardless of the underlying transactional volume. Access is gated by
 * the `dashboard` permission, consistent with the other dashboards.
 */
const BiDashboard = () => {
    const navigate = useNavigate();

    // KPI is owner-only — employees are redirected immediately.
    const ownerId = localStorage.getItem('ownerId');
    const isOwner = !ownerId;

    useEffect(() => {
        if (!isOwner) {
            navigate('/dashboard');
        }
    }, [isOwner, navigate]);

    // Shared period filter (defaults to last 30 days).
    const initial = useMemo(() => resolveQuickRange('30d'), []);
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [quickRange, setQuickRange] = useState('30d');
    const [rebuilding, setRebuilding] = useState(false);
    const [biBuilt, setBiBuilt] = useState<boolean | null>(null);

    // Forces a remount of every card to refetch on demand.
    const [refreshKey, setRefreshKey] = useState(0);
    const refreshAll = () => setRefreshKey((k) => k + 1);

    // Check whether the initial BI build has run.
    useEffect(() => {
        if (!isOwner) return;
        getBiStatus().then((s) => {
            if (s) setBiBuilt(s.built);
        });
    }, [isOwner]);

    const onRebuild = async () => {
        setRebuilding(true);
        const res = await triggerBiRebuild();
        setRebuilding(false);
        if (res) {
            const errors = res.results?.filter((r: any) => r.status === 'error') ?? [];
            if (errors.length > 0) {
                errors.forEach((e: any) => {
                    toast.error(
                        `[${e.name}] ${e.error ?? 'Erreur inconnue'}`,
                        { autoClose: false },
                    );
                });
            } else {
                toast.success('Recalcul des agrégats terminé avec succès.');
            }
            setBiBuilt(true);
            refreshAll();
        }
    };

    return (
        <div className="mt-3 h-full w-full">
            <GoBack />

            {/* Header */}
            <div className="mb-4 mt-2 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-lightPrimary text-3xl text-toola-500">
                        <MdInsights />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-navy-700">
                            Tableau de bord KPI
                        </h1>
                        <p className="text-sm text-gray-500">
                            Indicateurs clés calculés à partir d'agrégats pré-calculés
                        </p>
                    </div>
                </div>
            </div>

            {/*
             * The manual rebuild is only ever exposed through the bootstrap banner
             * below (first build only). It fully recomputes every KPI across every
             * owner (thousands in production) and can run for tens of minutes; a
             * persistent, always-visible trigger invites repeat/accidental clicks
             * from several admins that would each queue up behind the distributed
             * rebuild lock for no benefit, since the hourly/daily crons already
             * keep the data fresh on their own.
             */}

            {/* Banner when initial build hasn't run yet */}
            {isOwner && biBuilt === false && (
                <div className="mb-4 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                    <p className="text-sm text-amber-800">
                        Les données KPI ne sont pas encore calculées. Cliquez sur{' '}
                        <strong>Recalculer les agrégats</strong> pour lancer le premier calcul.
                    </p>
                    <button
                        onClick={onRebuild}
                        disabled={rebuilding}
                        className="ml-4 flex shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-50"
                    >
                        <MdRefresh className={rebuilding ? 'animate-spin' : ''} />
                        {rebuilding ? 'Calcul en cours...' : 'Calculer maintenant'}
                    </button>
                </div>
            )}

            {/* Global period filter */}
            <KpiFilters
                from={from}
                to={to}
                quickRange={quickRange}
                onQuickRange={setQuickRange}
                onFromChange={setFrom}
                onToChange={setTo}
                onRefresh={refreshAll}
            />

            {/* KPI grid */}
            <div key={refreshKey} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Row 1 — Revenue (#1) + Top products (#8) */}
                <div className="lg:col-span-1">
                    <RevenueChart from={from} to={to} />
                </div>
                <div className="lg:col-span-1">
                    <TopProducts from={from} to={to} />
                </div>

                {/* Row 2 — Prospect funnel (#5/#6) full width */}
                <div className="lg:col-span-2">
                    <ProspectFunnel from={from} to={to} />
                </div>

                {/* Row 3 — Product margin (#2) + Client margin (#3) */}
                <div className="lg:col-span-1">
                    <ProductMarginTable from={from} to={to} />
                </div>
                <div className="lg:col-span-1">
                    <ClientMarginTable from={from} to={to} />
                </div>

                {/* Row 4 — Unpaid invoices (#4) + Pareto (#7) */}
                <div className="lg:col-span-1">
                    <UnpaidInvoices />
                </div>
                <div className="lg:col-span-1">
                    <ParetoCard />
                </div>
            </div>
        </div>
    );
};

export default BiDashboard;
